/**
 * Générateur — Représenter des formules sous la forme d'une expression
 * informatique dans un langage de programmation par blocs.
 * Attendu ID : 998 | Niveau : 5ème | Domaine : La pensée informatique
 *
 * Libellé officiel (spec 998, 2026-09-21) : « Représenter des formules sous la
 * forme d'une expression informatique dans un langage de programmation par
 * blocs. »
 *
 * Plan de cartes (mise en conformité spécification, 2026-09-21) :
 *
 *   Carte « vers_blocs » — De la formule aux blocs. QCM à choix IMAGES : la
 *   formule est donnée en écriture mathématique, les choix sont des arbres de
 *   blocs emboîtés (un seul a l'arbre de la formule). Validation STRUCTURELLE :
 *   la valeur ne compte pas, deux distracteurs ont même valeur que la bonne
 *   réponse (structure fausse), la consigne le dit. Quatre niveaux tirés à
 *   égalité (voir « Graduation »).
 *
 *   Carte « vers_expr » — Du bloc à l'expression. Le sens inverse (le bloc est
 *   donné, l'élève choisit l'écriture mathématique) : la spec 1000 le renvoie
 *   explicitement à cet objectif (« l'objectif sur la représentation des
 *   formules, pris dans l'autre sens »). QCM de 3-4 écritures ; les mêmes
 *   erreurs, en miroir. Mêmes quatre niveaux.
 *
 *   Carte « variable » (ancien Ex.5, bloc avec « ma variable ») : SUPPRIMÉE,
 *   exclusion « Aucune variable : l'expression ne porte que des nombres ».
 *
 * Spécification appliquée :
 *
 *   Bornes : opérandes entiers dans [2 ; 30] ; diviseur (valeur) dans
 *   [2 ; 10], dividende multiple du diviseur (quotient entier) ; 1 à 3
 *   opérations ; profondeur d'emboîtement 1 à 3 ; opérateurs + − × ÷ ; une
 *   expression par item (nombre_d_expressions_par_exercice ∈ [1 ; 3]).
 *   Exclusions : aucune variable, aucune structure de contrôle, aucun opérande
 *   0 ou 1, aucun quotient non entier, aucune valeur intermédiaire négative
 *   (ici : toute valeur, intermédiaire ou finale, est un entier ≥ 2, à tous
 *   les niveaux), aucune évaluation numérique demandée.
 *
 *   Erreurs visées → mots-clés (code des distracteurs, _meta.params.distracteurs) :
 *     1. aplatir l'expression de gauche à droite (perd l'emboîtement)
 *          → enchainement_a_plat   (écritures AVEC parenthèses, niveau 3)
 *     2. chercher un bloc parenthèse et renoncer à traduire la priorité
 *          → pas_de_bloc_parenthese (l'arbre de l'écriture lue sans ses
 *            parenthèses, avec les priorités usuelles)
 *     3. emboîter dans le mauvais sens une soustraction ou une division
 *          → sens_soustraction_division (opérandes d'un nœud − ou ÷ échangés)
 *     4. valeur juste, structure fausse (une somme remplacée par sa valeur)
 *          → valeur_juste_structure_fausse (un sous-bloc remplacé par le
 *            nombre qu'il vaut)
 *     5. lire une écriture sans parenthèses de gauche à droite
 *          → lecture_gauche_droite (écritures SANS parenthèses, niveaux 2 et 4 ;
 *            même transformation que 1, sur l'autre type d'écriture)
 *     6. poser un seul bloc portant le résultat calculé de tête
 *          → resultat_seul (une pastille : la valeur de l'expression)
 *     + ordre_des_operandes : au niveau 1, pour + et ×, les deux opérandes
 *       échangés. Ce distracteur ne sert pas une erreur visée mais la règle du
 *       type_reponse (« la commutativité n'est pas admise : la formule
 *       demandée a un ordre ») — signalé dans le rapport.
 *   Dans « vers_expr », les mêmes codes désignent la même erreur vue depuis le
 *   bloc (l'écriture proposée est celle de l'arbre fautif).
 *
 *   Graduation → cartes (les deux cartes tirent le niveau à égalité) :
 *     niveau 1 : une opération (4 formes) ;
 *     niveau 2 : deux opérations, écriture sans parenthèses, la multiplication
 *                ou la division à emboîter dans l'addition ou la soustraction
 *                (8 formes) ;
 *     niveau 3 : trois opérations, écriture avec parenthèses, les cinq formes
 *                d'arbre à trois nœuds sont atteignables (10 formes) ;
 *     niveau 4 : trois opérations, écriture sans parenthèses, une soustraction
 *                ET une division (8 formes).
 *
 *   Non tranché (voir audit/5eme/998.md) : le type_reponse prescrit un éditeur
 *   de blocs embarqué (assemblage, arbres comparés) — format inexistant dans
 *   TanoEd, la QCM d'images en est l'approximation structurelle ; le squelette
 *   de démarrage (fourni aux niveaux 1-2, retiré au 4) est sans objet en QCM ;
 *   les valeurs intermédiaires négatives, admises au niveau 4 seulement,
 *   ne sont pas produites (la spec ne les exige pas).
 */

import { randomChoice, randInt, shuffle, makeQcm, tex } from '../../../utils';
import {
    imgElement, imgChoix, svgExpr, evalExpr, exprEnEcriture, cleExpr,
} from './svgScratch';

const ATTENDU_ID = 998;

const L_VERS_BLOCS = 'De la formule aux blocs';
const L_VERS_EXPR = "Du bloc à l'expression";

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Bornes de la spécification ──────────────────────────────

const OPERANDE_MIN = 2;
const OPERANDE_MAX = 30;
const DIVISEUR_MIN = 2;
const DIVISEUR_MAX = 10;
const VALEUR_MIN = 2;          // toute valeur (intermédiaire ou finale) : entier ≥ 2

// ─── Outils d'arbre ──────────────────────────────────────────

const noeud = (o, a, b) => ({ op: o, a, b });
const estOp = (n) => typeof n === 'object' && n !== null;

/** Feuilles dans l'ordre de l'écriture. */
const feuilles = (n) => (estOp(n) ? [...feuilles(n.a), ...feuilles(n.b)] : [n]);
/** Opérateurs dans l'ordre de l'écriture (parcours infixe). */
const operateurs = (n) => (estOp(n) ? [...operateurs(n.a), n.op, ...operateurs(n.b)] : []);
const nbOperations = (n) => (estOp(n) ? 1 + nbOperations(n.a) + nbOperations(n.b) : 0);
const profondeur = (n) => (estOp(n) ? 1 + Math.max(profondeur(n.a), profondeur(n.b)) : 0);
/** Tous les nœuds opération, racine en tête. */
const sousExpressions = (n) => (estOp(n) ? [n, ...sousExpressions(n.a), ...sousExpressions(n.b)] : []);
const estNonCommutatif = (o) => o === '−' || o === '/';

/** Copie de l'arbre où le nœud `cible` (identité) est remplacé par `nouveau`. */
const remplacer = (n, cible, nouveau) => {
    if (n === cible) return nouveau;
    return estOp(n) ? noeud(n.op, remplacer(n.a, cible, nouveau), remplacer(n.b, cible, nouveau)) : n;
};

/** Écriture mathématique (÷ au lieu du / des blocs). */
const ecriture = (ast) => exprEnEcriture(ast).replace(/ \/ /g, ' ÷ ');
/** La même en KaTeX pour l'énoncé. */
const ecritureTex = (ast) => tex(ecriture(ast).replace(/×/g, '\\times').replace(/÷/g, '\\div').replace(/−/g, '-'));

/**
 * Validité selon la spec : opérandes entiers dans [2 ; 30], diviseur (valeur)
 * dans [2 ; 10], chaque sous-expression et l'expression entière valent un
 * entier ≥ 2 (quotients entiers, rien de négatif, ni 0 ni 1).
 */
const valide = (ast) => {
    if (!feuilles(ast).every((x) => Number.isInteger(x) && x >= OPERANDE_MIN && x <= OPERANDE_MAX)) return false;
    return sousExpressions(ast).every((s) => {
        const v = evalExpr(s);
        if (!Number.isInteger(v) || v < VALEUR_MIN) return false;
        if (s.op === '/') {
            const d = evalExpr(s.b);
            return d >= DIVISEUR_MIN && d <= DIVISEUR_MAX;
        }
        return true;
    });
};

// ─── Tirages élémentaires ────────────────────────────────────

const r = randInt;
const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));

/** Division à quotient entier sur deux feuilles : diviseur d ∈ [2 ; 10], dividende d × q ≤ 30. */
const tirerDivision = (qMin = 2, qMax = 15) => {
    const d = r(DIVISEUR_MIN, DIVISEUR_MAX);
    const haut = Math.min(qMax, Math.floor(OPERANDE_MAX / d));
    if (haut < qMin) return null;
    const q = r(qMin, haut);
    return { n: d * q, d, q };
};

/** Deux feuilles b, c ∈ [2 ; 30] de somme s (s ∈ [4 ; 60]). */
const sommeDeDeux = (s) => {
    const b = r(Math.max(OPERANDE_MIN, s - OPERANDE_MAX), Math.min(OPERANDE_MAX, s - OPERANDE_MIN));
    return { b, c: s - b };
};

// ─── Formes par niveau ───────────────────────────────────────
// Chaque forme : { id, niveau, tirer() → arbre ou null }. Construction autant
// que possible ; `valide` reste le juge final (tirage rejeté et repris sinon).

const A = () => r(OPERANDE_MIN, OPERANDE_MAX);

const FORMES = [
    // ── niveau 1 : une opération ──
    { id: 'n1_plus', niveau: 1, tirer: () => noeud('+', A(), A()) },
    { id: 'n1_moins', niveau: 1, tirer: () => { const b = r(2, 28); return noeud('−', r(b + 2, 30), b); } },
    { id: 'n1_fois', niveau: 1, tirer: () => noeud('×', A(), A()) },
    { id: 'n1_div', niveau: 1, tirer: () => { const t = tirerDivision(); return t && noeud('/', t.n, t.d); } },

    // ── niveau 2 : deux opérations, sans parenthèses, la prioritaire à emboîter ──
    { id: 'n2_plus_fois', niveau: 2, tirer: () => noeud('+', A(), noeud('×', A(), A())) },
    { id: 'n2_fois_plus', niveau: 2, tirer: () => noeud('+', noeud('×', A(), A()), A()) },
    { id: 'n2_moins_fois', niveau: 2, tirer: () => {
        const c = r(2, 14), b = r(2, Math.floor(28 / c));
        return noeud('−', r(b * c + 2, 30), noeud('×', b, c));
    } },
    { id: 'n2_fois_moins', niveau: 2, tirer: () => {
        const b = r(2, 15), c = r(2, Math.floor(30 / b));
        if (b * c < 4) return null;
        return noeud('−', noeud('×', b, c), r(2, b * c - 2));
    } },
    { id: 'n2_plus_div', niveau: 2, tirer: () => { const t = tirerDivision(); return t && noeud('+', A(), noeud('/', t.n, t.d)); } },
    { id: 'n2_div_plus', niveau: 2, tirer: () => { const t = tirerDivision(); return t && noeud('+', noeud('/', t.n, t.d), A()); } },
    { id: 'n2_moins_div', niveau: 2, tirer: () => { const t = tirerDivision(); return t && noeud('−', r(t.q + 2, 30), noeud('/', t.n, t.d)); } },
    { id: 'n2_div_moins', niveau: 2, tirer: () => { const t = tirerDivision(4); return t && noeud('−', noeud('/', t.n, t.d), r(2, t.q - 2)); } },

    // ── niveau 3 : trois opérations, écriture AVEC parenthèses, cinq formes d'arbre ──
    { id: 'n3_eq_somme_fois_diff', niveau: 3, tirer: () => {      // (a + b) × (c − d)
        const d = r(2, 28);
        return noeud('×', noeud('+', A(), A()), noeud('−', r(d + 2, 30), d));
    } },
    { id: 'n3_eq_diff_div_somme', niveau: 3, tirer: () => {       // (a − b) ÷ (c + d)
        const c = r(2, 8), d = r(2, 10 - c), s = c + d;
        const q = r(2, Math.floor(28 / s));
        const b = r(2, 30 - q * s);
        return noeud('/', noeud('−', b + q * s, b), noeud('+', c, d));
    } },
    { id: 'n3_gg_somme_fois_moins', niveau: 3, tirer: () => {     // (a + b) × c − d
        const g = noeud('×', noeud('+', A(), A()), A());
        return noeud('−', g, r(2, Math.min(30, evalExpr(g) - 2)));
    } },
    { id: 'n3_gg_diff_div_plus', niveau: 3, tirer: () => {        // (a − b) ÷ c + d
        const c = r(2, 10), q = r(2, Math.floor(28 / c));
        const b = r(2, 30 - q * c);
        return noeud('+', noeud('/', noeud('−', b + q * c, b), c), A());
    } },
    { id: 'n3_gd_fois_somme_plus', niveau: 3, tirer: () => noeud('+', noeud('×', A(), noeud('+', A(), A())), A()) },   // a × (b + c) + d
    { id: 'n3_gd_fois_diff_plus', niveau: 3, tirer: () => {       // a × (b − c) + d
        const c = r(2, 28);
        return noeud('+', noeud('×', A(), noeud('−', r(c + 2, 30), c)), A());
    } },
    { id: 'n3_dg_plus_diff_fois', niveau: 3, tirer: () => {       // a + (b − c) × d
        const c = r(2, 28);
        return noeud('+', A(), noeud('×', noeud('−', r(c + 2, 30), c), A()));
    } },
    { id: 'n3_dg_plus_somme_div', niveau: 3, tirer: () => {       // a + (b + c) ÷ d
        const d = r(2, 10), q = r(2, Math.floor(58 / d));
        const { b, c } = sommeDeDeux(q * d);
        return noeud('+', A(), noeud('/', noeud('+', b, c), d));
    } },
    { id: 'n3_dd_moins_somme_fois', niveau: 3, tirer: () => {     // a − (b + c × d)
        const c = r(2, 6), d = r(2, Math.floor(24 / c)), p = c * d;
        const b = r(2, 28 - p);
        return noeud('−', r(b + p + 2, 30), noeud('+', b, noeud('×', c, d)));
    } },
    { id: 'n3_dd_moins_somme_div', niveau: 3, tirer: () => {      // a − (b + c) ÷ d
        const d = r(2, 10), q = r(2, Math.min(28, Math.floor(58 / d)));
        const { b, c } = sommeDeDeux(q * d);
        return noeud('−', r(q + 2, 30), noeud('/', noeud('+', b, c), d));
    } },

    // ── niveau 4 : trois opérations, SANS parenthèses, une soustraction ET une division ──
    { id: 'n4_eq_fois_moins_div', niveau: 4, tirer: () => {       // a × b − n ÷ d
        const t = tirerDivision();
        return t && noeud('−', noeud('×', A(), A()), noeud('/', t.n, t.d));
    } },
    { id: 'n4_eq_div_moins_fois', niveau: 4, tirer: () => {       // n ÷ d − b × c
        const d = r(2, 5), q = r(6, Math.floor(30 / d));
        const b = r(2, Math.floor((q - 2) / 2)), c = r(2, Math.floor((q - 2) / b));
        return noeud('−', noeud('/', d * q, d), noeud('×', b, c));
    } },
    { id: 'n4_eq_moins_moins_div', niveau: 4, tirer: () => {      // a − b − n ÷ d
        const t = tirerDivision(2, 13);
        if (!t) return null;
        const b = r(2, 28 - t.q);
        return noeud('−', noeud('−', r(b + t.q + 2, 30), b), noeud('/', t.n, t.d));
    } },
    { id: 'n4_gd_moins_div_plus', niveau: 4, tirer: () => {       // a − n ÷ d + c
        const t = tirerDivision();
        return t && noeud('+', noeud('−', r(t.q + 2, 30), noeud('/', t.n, t.d)), A());
    } },
    { id: 'n4_gd_plus_div_moins', niveau: 4, tirer: () => {       // a + n ÷ d − c
        const t = tirerDivision();
        if (!t) return null;
        const a = A();
        return noeud('−', noeud('+', a, noeud('/', t.n, t.d)), r(2, Math.min(30, a + t.q - 2)));
    } },
    { id: 'n4_dg_moins_fois_div', niveau: 4, tirer: () => {       // a − b × c ÷ d
        const d = r(2, 10), b = A();
        const m = d / pgcd(b, d);
        const c = m === 1 ? A() : m * r(1, Math.floor(30 / m));
        const q = (b * c) / d;
        if (q < 2 || q > 28) return null;
        return noeud('−', r(q + 2, 30), noeud('/', noeud('×', b, c), d));
    } },
    { id: 'n4_gg_div_fois_moins', niveau: 4, tirer: () => {       // n ÷ d × c − b
        const t = tirerDivision();
        if (!t) return null;
        const c = A();
        return noeud('−', noeud('×', noeud('/', t.n, t.d), c), r(2, Math.min(30, t.q * c - 2)));
    } },
    { id: 'n4_gg_div_plus_moins', niveau: 4, tirer: () => {       // n ÷ d + a − b
        const t = tirerDivision();
        if (!t) return null;
        const a = A();
        return noeud('−', noeud('+', noeud('/', t.n, t.d), a), r(2, Math.min(30, t.q + a - 2)));
    } },
];

/** Replis déterministes, un par niveau, vérifiés par `valide` et par le self-test. */
const REPLIS = {
    1: { id: 'n1_moins', ast: noeud('−', 12, 5) },
    2: { id: 'n2_plus_fois', ast: noeud('+', 4, noeud('×', 6, 3)) },
    3: { id: 'n3_eq_somme_fois_diff', ast: noeud('×', noeud('+', 5, 7), noeud('−', 9, 3)) },
    4: { id: 'n4_gg_div_plus_moins', ast: noeud('−', noeud('+', noeud('/', 20, 4), 9), 6) },
};

// ─── Arbres fautifs (un par erreur visée) ────────────────────

/** Erreurs 1 et 5 : les jetons de l'écriture enchaînés de gauche à droite, parenthèses et priorités ignorées. */
const enchainerAPlat = (ast) => {
    const f = feuilles(ast), ops = operateurs(ast);
    return ops.reduce((t, o, i) => noeud(o, t, f[i + 1]), f[0]);
};

/** Erreur 2 : l'écriture lue SANS ses parenthèses, avec les priorités usuelles. */
const lireSansParentheses = (ast) => {
    const f = feuilles(ast), ops = operateurs(ast);
    const termes = [f[0]];
    const additifs = [];
    ops.forEach((o, i) => {
        if (o === '×' || o === '/') termes[termes.length - 1] = noeud(o, termes[termes.length - 1], f[i + 1]);
        else { additifs.push(o); termes.push(f[i + 1]); }
    });
    return additifs.reduce((t, o, i) => noeud(o, t, termes[i + 1]), termes[0]);
};

/** Erreur 3 : un nœud − ou ÷ (au hasard) avec ses opérandes échangés. */
const echangerSens = (ast) => {
    const cibles = sousExpressions(ast).filter((s) => estNonCommutatif(s.op));
    if (!cibles.length) return null;
    const c = randomChoice(cibles);
    return remplacer(ast, c, noeud(c.op, c.b, c.a));
};

/** Erreur 4 : un sous-bloc (jamais la racine) remplacé par le nombre qu'il vaut. */
const remplacerParValeur = (ast) => {
    const internes = sousExpressions(ast).slice(1);
    if (!internes.length) return null;
    const c = randomChoice(internes);
    return remplacer(ast, c, evalExpr(c));
};

/** Règle du type_reponse (niveau 1, + et ×) : les deux opérandes échangés. */
const echangerOrdre = (ast) => noeud(ast.op, ast.b, ast.a);

const candidatsFautifs = (ast, forme) => {
    const avecParentheses = ecriture(ast).includes('(');
    const liste = [
        { code: avecParentheses ? 'enchainement_a_plat' : 'lecture_gauche_droite', arbre: enchainerAPlat(ast) },
        { code: 'pas_de_bloc_parenthese', arbre: avecParentheses ? lireSansParentheses(ast) : null },
        { code: 'sens_soustraction_division', arbre: echangerSens(ast) },
        { code: 'valeur_juste_structure_fausse', arbre: remplacerParValeur(ast) },
        { code: 'resultat_seul', arbre: evalExpr(ast) },
        { code: 'ordre_des_operandes', arbre: forme.niveau === 1 && !estNonCommutatif(ast.op) ? echangerOrdre(ast) : null },
    ];
    return liste.filter((c) => c.arbre !== null && c.arbre !== undefined);
};

/**
 * Tirage complet : niveau à égalité, forme du niveau, arbre valide, puis les
 * distracteurs distincts (par `cle`) de la bonne réponse et entre eux. Boucle
 * bornée, repli déterministe par niveau.
 */
const tirerExpression = (cle) => {
    const niveau = r(1, 4);
    const formes = FORMES.filter((f) => f.niveau === niveau);
    const assembler = (forme, ast) => {
        const bonne = cle(ast);
        const vus = new Set([bonne]);
        const distracteurs = shuffle(candidatsFautifs(ast, forme)).filter((c) => {
            const k = cle(c.arbre);
            if (vus.has(k)) return false;
            vus.add(k);
            return true;
        }).slice(0, 3);
        return { forme, ast, distracteurs };
    };
    for (let i = 0; i < 80; i++) {
        const forme = randomChoice(formes);
        const ast = forme.tirer();
        if (!ast || !valide(ast)) continue;
        const res = assembler(forme, ast);
        if (res.distracteurs.length >= 2) return res;
    }
    const repli = REPLIS[niveau];
    const res = assembler(FORMES.find((f) => f.id === repli.id), repli.ast);
    if (!valide(repli.ast) || res.distracteurs.length < 2) throw new Error(`gen_998 : repli invalide au niveau ${niveau}`);
    return res;
};

const params = ({ forme, ast, distracteurs }) => ({
    niveau: forme.niveau,
    forme: forme.id,
    parentheses: ecriture(ast).includes('('),
    nb_operations: nbOperations(ast),
    profondeur: profondeur(ast),
    operandes: feuilles(ast),
    diviseurs: sousExpressions(ast).filter((s) => s.op === '/').map((s) => evalExpr(s.b)),
    intermediaires: sousExpressions(ast).map(evalExpr),
    valeur: evalExpr(ast),
    ecriture: ecriture(ast),
    distracteurs: distracteurs.map((d) => ({ code: d.code, ecriture: ecriture(d.arbre) })),
});

// ═════════ Carte 1 — De la formule aux blocs ═════════

const genVersBlocsQcm = () => {
    const t = tirerExpression(cleExpr);
    const texte = `<p><b>Quel bloc représente la formule ${ecritureTex(t.ast)} ?</b></p>`
        + '<p>Représente-la telle qu\'elle est écrite : les mêmes nombres, dans le même ordre, '
        + 'sans la calculer.</p>';
    const choixCorrect = imgChoix(svgExpr(t.ast));
    const choix = [choixCorrect, ...t.distracteurs.map((d) => imgChoix(svgExpr(d.arbre)))];
    const payload = makeQcm(choix, choixCorrect, texte, opts(L_VERS_BLOCS));
    payload._meta.params = params(t);
    return payload;
};

// ═════════ Carte 2 — Du bloc à l'expression ═════════

const genVersExprQcm = () => {
    const t = tirerExpression(ecriture);
    const texte = '<p><b>Quelle écriture mathématique correspond au bloc ci-dessous ?</b></p>'
        + '<p>Choisis l\'écriture qui a les mêmes nombres, dans le même ordre, et qui calcule '
        + 'dans le même ordre que le bloc.</p>';
    const bonne = ecriture(t.ast);
    const ecritures = [bonne, ...t.distracteurs.map((d) => ecriture(d.arbre))];
    const payload = makeQcm(ecritures, bonne, texte,
        { ...opts(L_VERS_EXPR), elements_additionnels: imgElement(svgExpr(t.ast)) });
    payload._meta.params = params(t);
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ───────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'validation structurelle : deux distracteurs (valeur_juste_structure_fausse, resultat_seul) ont la MÊME valeur que la bonne réponse, la consigne dit « sans la calculer »',
        'chaque distracteur porte son code dans _meta.params.distracteurs ; 2 à 3 distracteurs, tirés en rotation parmi les erreurs disponibles pour la forme',
        'jamais de bouche-trou : si moins de deux distracteurs distincts, nouveau tirage complet',
    ],
    vers_blocs_qcm_unique: 'QCM d\'IMAGES : arbres de blocs ; clé d\'unicité = structure (cleExpr)',
    vers_expr_qcm_unique: 'QCM d\'écritures : la même erreur vue depuis le bloc ; clé d\'unicité = écriture rendue',
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    vers_blocs_qcm_unique: genVersBlocsQcm,
    vers_expr_qcm_unique: genVersExprQcm,
};

const FORMAT_LABELS = { qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'vers_blocs', label: L_VERS_BLOCS, formatsList: buildFormats('vers_blocs') },
    { id: 'vers_expr', label: L_VERS_EXPR, formatsList: buildFormats('vers_expr') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
